<i18n src="@/locales/modules/PostTranslate.json"></i18n>

<template>
	<div class="post-item p-6 pb-0 overflow-hidden">
		<div class="header post-item__header flex items-start justify-between">
			<div class="content header__content flex flex-wrap sm:flex-no-wrap">
				<vs-avatar class="m-0 mr-5" size="large" :src="value.authorAvatar" />
				<div class="flex flex-col info content__info">
					<div class="flex prople info__people flex-wrap md:flex-no-wrap">
						<span class="font-bold uppercase">{{ value.authorName }}</span>
						<div class="people__tagged flex flex-wrap sm:flex-no-wrap" v-if="value.peopleTagged && value.peopleTagged.length">
							<span class="mx-1 whitespace-no-wrap">{{ $t("With").toLowerCase() }}</span>
							<span class="font-bold uppercase truncate" :title="value.peopleTagged.join(', ')">{{ value.peopleTagged.join(", ") }}</span>
						</div>
					</div>
					<div :class="['info__date text-gray-3 flex items-center self-start', { 'mb-2': value.categoryName }]">
						<i class="nucleo nucleo-clock-2 text-solid-gray-1"></i>
						<span class="ml-1" :title="this.value.dateInfo | dateTime(userDateTimeFormat)">{{ dateInfoFormatted }}</span>
					</div>
					<span class="header__category self-start" v-if="value.categoryName">{{ value.categoryName }}</span>
				</div>
			</div>

			<!-- <vs-dropdown class="header__actions cursor-pointer self-start" v-if="value.canEdit || value.canDelete" vs-trigger-click vs-custom-content>
				<vs-dropdown-menu>
					<vs-dropdown-item class="text-center" v-if="value.canEdit">
						<div type="filled" class="w-32 font-bold" @click="$emit('onClickEdit', value)">
							{{ $t("Actions.Edit") }}
						</div>
					</vs-dropdown-item>
					<vs-dropdown-item class="text-center" v-if="value.canDelete">
						<div type="filled" class="w-32 font-bold" @click="showDeleteConfirm(value)">
							{{ $t("Actions.Delete") }}
						</div>
					</vs-dropdown-item>
				</vs-dropdown-menu>
			</vs-dropdown> -->
			<BasicContextMenu type="vertical" :target="value" @onClickEdit="value => $emit('onClickEdit', value)" @onClickDelete="showDeleteConfirm" />
		</div>

		<div class="post-item__content my-5">
			<div
				ref="postContent"
				class="ql-editor"
				:style="{
					maxHeight: this.isReadmore ? `${this.maxContentHeight}px` : 'unset',
					overflow: 'hidden'
				}"
				v-html="value.content"
			></div>
			<a href="#" class="underline text-gray-2 italic" v-if="isShowReadmore" @click.prevent="isReadmore = !isReadmore">{{ $t(isReadmore ? "Readmore" : "Readless") }}</a>
		</div>

		<div class="attachments post-item__attachments" v-if="value.attachments && value.attachments.length > 0">
			<div class="attachments__images" v-if="attachImages && attachImages.length > 0">
				<img :src="file.path" :alt="file.file_name" :class="['w-full', { 'max-w-md mr-5 last:mr-0 mb-5': attachImages.length > 1 }]" v-for="file in attachImages" :key="file.id" />
			</div>

			<div class="files attachments__files flex flex-wrap mt-5" v-if="attachFiles && attachFiles.length > 0">
				<span
					class="files__item cursor-pointer mr-3 mb-3 last:mr-0 flex items-center sm:max-w-md"
					v-for="file in attachFiles"
					:key="file.id"
					:title="`${$t('Actions.Download')} ${file.file_name}`"
					@click.prevent="handleViewFile(file)"
				>
					<i class="nucleo nucleo-link text-gray-2 mr-3"></i>
					<span class="text-black truncate">{{ file.file_name }}</span>
				</span>
			</div>
		</div>

		<div class="footer post-item__footer mt-3 -mx-6 flex py-3 pl-6">
			<div class="like footer__like flex items-center mr-6">
				<i
					:title="$t('LikeAction')"
					:class="[
						'cursor-pointer text-2xl mr-3 nucleo hover:text-solid-primary',
						{
							'nucleo-heart text-gray-3': !value.liked,
							'nucleo-favorite': value.liked
						}
					]"
					@click="handleLikeAction"
				></i>
				<span :class="['like__number', { 'text-solid-primary': value.liked }]">{{ value.likes }} {{ $tc("Like", value.likes).toLowerCase() }}</span>
			</div>

			<div class="comment footer__comment flex items-center">
				<i
					:title="$t('CommentAction')"
					:class="[
						'cursor-pointer text-2xl mr-3 nucleo hover:text-solid-primary',
						{
							'nucleo-b-chat text-gray-3': !isShowComments,
							'nucleo-b-chat-2': isShowComments
						}
					]"
					@click="toggleShowComment"
				></i>
				<span class="comment__number">{{ value.totalComment }} {{ $tc("Comment", value.totalComment).toLowerCase() }}</span>
			</div>
		</div>

		<transition name="slide" mode="out-in" tag="div">
			<CbsComment
				v-if="isShowComments"
				class="post-item__comments"
				displayMode="post-comments"
				:postId="value.id"
				:model="$constant.RELATION_MODELS.POST"
				@onSentComment="data => $emit('onSentComment', data)"
			/>
		</transition>
	</div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { showConfirm } from "@/mixins/mixins";

import CbsComment from "@/components/cbs-comment/CbsComment";
import BasicContextMenu from "@/components/basic-context-menu/BasicContextMenu";

export default {
	name: "PostItem",
	mixins: [showConfirm],
	components: { CbsComment, BasicContextMenu },
	props: {
		value: {
			type: Object,
			required: true,
			validator: prop =>
				_.has(prop, "id") &&
				_.has(prop, "liked") &&
				_.has(prop, "likes") &&
				_.has(prop, "content") &&
				_.has(prop, "can_edit") &&
				_.has(prop, "dateInfo") &&
				_.has(prop, "can_delete") &&
				_.has(prop, "authorName") &&
				_.has(prop, "categoryName") &&
				_.has(prop, "totalComment") &&
				_.has(prop, "peopleTagged") &&
				_.has(prop, "authorAvatar") &&
				_.has(prop, "attachments")
		},
		maxContentHeight: {
			type: Number,
			default: 200
		}
	},
	emits: {
		onClickEdit: {},
		onClickDelete: {},
		onSentComment: {}
	},
	data() {
		return {
			isReadmore: false,
			isShowReadmore: false,
			isShowComments: false
		};
	},
	computed: {
		attachImages() {
			return this.value.attachments.filter(file => this.fileIsImage(file.extension));
		},
		attachFiles() {
			return this.value.attachments.filter(file => !this.fileIsImage(file.extension));
		},
		dateInfoFormatted() {
			moment.locale(this.userSettings && this.userSettings.locale === "vn" ? "vi" : "en");
			const dateDiff = moment().diff(this.value.dateInfo, "days");
			return this.value.dateInfo && dateDiff >= 1 ? moment(this.value.dateInfo).fromNow() : this.$options.filters.dateTime(this.value.dateInfo, this.userDateTimeFormat);
		}
	},
	methods: {
		handleLikeAction() {
			clearTimeout(this.toggleLikeTimer);
			this.toggleLikeTimer = setTimeout(async () => {
				try {
					this.$vs.loading();
					await this.$store.dispatch("postsManagement/toggleLikePost", this.value.id);
				} catch (error) {
					this.$helper.notifyError({ context: this, error });
				} finally {
					this.$nextTick(() => this.$vs.loading.close());
				}
			}, 200);
		},
		toggleShowComment() {
			this.isShowComments = !this.isShowComments;
		},
		showDeleteConfirm(value) {
			this.mixinShowConfirm().then(isAccept => {
				if (isAccept) {
					this.$emit("onClickDelete", value);
				}
			});
		},
		handleViewFile(file) {
			if (file.extension === "pdf") {
				window.open(file.path, "_blank");
			} else if (file.extension === "ppt" || file.extension === "pptx" || file.extension === "doc" || file.extension === "docx" || file.extension === "xls" || file.extension === "xlsx") {
				window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${file.path}`);
			}
		}
	},
	mounted() {
		if (this.$refs.postContent) {
			const postContentHeight = this.$refs.postContent.scrollHeight;
			this.isShowReadmore = this.isReadmore = postContentHeight > this.maxContentHeight;
		}
	}
};
</script>

<style lang="scss" scoped>
.post-item {
	border: 1px solid #dbdbdb;
	border-radius: 8px;

	.header__content {
		width: 100%;
		max-width: 99%;
	}

	.header__category {
		font-size: 14px;
		line-height: 18px;
		color: var(--color-gray-2);
		border: 1px solid var(--color-gray-2);
		border-radius: 3px;
		padding: 6px 17px;
	}

	.people__tagged {
		max-width: 90%;
	}

	.content__info {
		max-width: calc(100% - 70px);
	}

	.post-item__footer {
		background-color: #f4f4f4;
	}

	.files__item {
		background: var(--color-gray-6);
		border-radius: 20px;
		padding: 9px 24px;

		@media screen and (max-width: 576px) {
			max-width: 230px;
		}
	}

	.header__actions {
		&::after {
			content: "\2807";
			font-size: 20px;
			line-height: 100%;
			margin-right: -8px;
			color: var(--color-solid-primary);
		}
	}
}

.slide-enter-active {
	animation: slide-from-right 0.3s ease-in-out;
}

.slide-leave-active {
	animation: slide-from-left 0.5s ease-in-out;
}

@keyframes slide-from-right {
	0% {
		transform: translateX(200%);
	}
	100% {
		transform: translateX(0);
	}
}

@keyframes slide-from-left {
	0% {
		transform: translateX(0);
	}
	100% {
		transform: translateX(200%);
	}
}
</style>
